/**
 * AdminSystemHealth — /admin/health
 * =================================
 * System-Selbstdiagnose mit One-Click-Fix.
 *
 * Zeigt alle bekannten „Deploy-Fallstricke" auf einen Blick:
 * - WP-Katalog befüllt?
 * - Firmenanschrift plausibel (nicht Alzey/leer)?
 * - E-Mail-Absender + SMTP konfiguriert?
 * - Ref-Preise gepflegt?
 * - Stripe LIVE/TEST?
 * - Angebots-Historie (Info)
 *
 * Wo möglich: 1-Klick-Fix-Button. Alle Fixes sind idempotent.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, ShieldAlert, RefreshCw,
  ChevronDown, ChevronRight, Zap, ExternalLink,
  CheckCircle2, AlertTriangle, XCircle, Info, Mail,
} from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { SignatureAlertsPanel } from "@/components/SignatureAlertsPanel";

const STATUS_STYLES = {
  ok: {
    icon: CheckCircle2,
    color: "text-emerald-400",
    bg: "bg-emerald-500/8",
    border: "border-emerald-500/30",
    label: "OK",
    labelBg: "bg-emerald-500/20 text-emerald-300",
  },
  warn: {
    icon: AlertTriangle,
    color: "text-amber-400",
    bg: "bg-amber-500/8",
    border: "border-amber-500/40",
    label: "Warnung",
    labelBg: "bg-amber-500/20 text-amber-300",
  },
  error: {
    icon: XCircle,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/50",
    label: "Fehler",
    labelBg: "bg-red-500/25 text-red-300",
  },
  info: {
    icon: Info,
    color: "text-sky-300",
    bg: "bg-sky-500/8",
    border: "border-sky-500/30",
    label: "Info",
    labelBg: "bg-sky-500/20 text-sky-300",
  },
};

function CheckCard({ check, onFix, fixingId, onToggle, expanded }) {
  const style = STATUS_STYLES[check.status] || STATUS_STYLES.info;
  const Icon = style.icon;
  const isFixing = fixingId === check.id;
  const hasDetails = (check.details?.tenants || []).length > 0;

  return (
    <div
      data-testid={`sh-check-${check.id}`}
      className={`rounded-2xl border ${style.border} ${style.bg} p-4 sm:p-5 transition-colors`}
    >
      <div className="flex items-start gap-3">
        <Icon size={22} className={`${style.color} shrink-0 mt-0.5`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1">
            <span className="font-display font-bold text-[15px] text-zinc-100">{check.title}</span>
            <span className={`text-[9.5px] uppercase tracking-widest rounded px-1.5 py-0.5 font-bold ${style.labelBg}`}>
              {style.label}
            </span>
            <span className="text-[10.5px] text-zinc-500">· {check.category}</span>
          </div>
          <p className="text-[12.5px] text-zinc-300 leading-relaxed">{check.message}</p>

          {hasDetails && (
            <button
              type="button"
              onClick={() => onToggle(check.id)}
              data-testid={`sh-toggle-${check.id}`}
              className="mt-2 inline-flex items-center gap-1 text-[12px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200"
            >
              {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {expanded ? "Details ausblenden" : `Details anzeigen (${check.details.tenants.length})`}
            </button>
          )}

          {expanded && hasDetails && (
            <div className="mt-2 space-y-1" data-testid={`sh-details-${check.id}`}>
              {check.details.tenants.map((t, i) => {
                const s = STATUS_STYLES[t.status || check.status] || style;
                return (
                  <div key={t.tenant_id || i}
                       className="text-[11.5px] rounded-lg bg-[#09090B]/70 border border-zinc-800 px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-100 truncate">{t.name || t.tenant_id}</div>
                      {t.message && <div className="text-[10.5px] text-zinc-400 truncate">{t.message}</div>}
                      {t.address && (
                        <div className="text-[10.5px] text-zinc-500 truncate">
                          {t.address.street || "—"} · {t.address.zip || ""} {t.address.city || ""}
                        </div>
                      )}
                      {(t.components !== undefined) && (
                        <div className="text-[10.5px] text-zinc-500">
                          {t.components ?? 0} Komponenten · {t.packages ?? 0} Pakete
                        </div>
                      )}
                      {(t.with_ref !== undefined) && (
                        <div className="text-[10.5px] text-zinc-500">
                          {t.with_ref}/{t.total} Items mit Ref-Preis
                        </div>
                      )}
                      {t.email && (
                        <div className="text-[10.5px] text-zinc-500">
                          {t.email}{t.smtp_host ? ` · SMTP ${t.smtp_host}` : ""}
                        </div>
                      )}
                      {(t.latest_offer_number || t.count) && (
                        <div className="text-[10.5px] text-zinc-500">
                          {t.count} Angebot(e){t.latest_offer_number ? ` · zuletzt ${t.latest_offer_number}` : ""}
                        </div>
                      )}
                    </div>
                    {t.status && (
                      <span className={`text-[9.5px] uppercase tracking-widest rounded px-1.5 py-0.5 font-bold shrink-0 ${s.labelBg}`}>
                        {STATUS_STYLES[t.status]?.label || t.status}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {check.has_fix && (
          <button
            type="button"
            onClick={() => onFix(check.id)}
            disabled={isFixing}
            data-testid={`sh-fix-${check.id}`}
            className="h-9 px-3 shrink-0 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[11.5px] uppercase tracking-widest hover:bg-[#00C964] active:scale-[0.97] transition inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {isFixing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
            Fixen
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminSystemHealth() {
  const [loading, setLoading] = useState(true);
  const [overall, setOverall] = useState("ok");
  const [checks, setChecks] = useState([]);
  const [fixingId, setFixingId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/system-health/checks");
      setChecks(data.checks || []);
      setOverall(data.overall_status || "ok");
      setLastRun(new Date());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Health-Check fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const runFix = async (id) => {
    const isMigration = id === "wp_catalog_seeded";
    const warn = isMigration
      ? "⚠️ ACHTUNG — Legacy-Migration:\n\n" +
        "Diese Aktion schreibt nur FEHLENDE Legacy-Positionen nach. " +
        "Manuell gepflegte Komponenten/Pakete (mit `source=manual` oder mit `updated_by` gesetzt) werden NICHT angerührt.\n\n" +
        "Falls Sie unsicher sind, ob Ihre manuellen Preise korrekt geschützt sind, " +
        "klicken Sie zuerst unten den blauen Button 'Manuelle Einträge schützen' — dann sind Sie 100 % safe.\n\n" +
        "Fix jetzt ausführen?"
      : "Fix jetzt ausführen?\n\n" +
        "Alle Fixes sind idempotent und schreiben nur, wenn tatsächlich Handlungsbedarf besteht. " +
        "Manuell abweichende Werte bleiben unberührt.";
    if (!window.confirm(warn)) return;
    setFixingId(id);
    try {
      const { data } = await api.post(`/admin/system-health/fix/${id}`);
      toast.success("Fix ausgeführt — Status wird neu geladen.");
      // update the single check inline (schneller als voller reload)
      if (data.new_state) {
        setChecks((prev) => {
          const next = prev.map((c) => (c.id === id ? { ...data.new_state, has_fix: data.new_state.fixable } : c));
          next.sort((a, b) => (b.severity || 0) - (a.severity || 0) || (a.title || "").localeCompare(b.title || ""));
          return next;
        });
      }
      // Overall neu berechnen
      const worst = ["info", "ok", "warn", "error"];
      const ordinal = (s) => worst.indexOf(s);
      let ov = "ok";
      (data.new_state ? checks.map((c) => (c.id === id ? data.new_state : c)) : checks)
        .forEach((c) => { if (ordinal(c.status) > ordinal(ov)) ov = c.status; });
      setOverall(ov);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fix fehlgeschlagen");
    } finally {
      setFixingId(null);
    }
  };

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const [protecting, setProtecting] = useState(false);
  const [protectResult, setProtectResult] = useState(null);
  const protectManualEdits = async () => {
    if (!window.confirm(
      "Manuell gepflegte Komponenten/Pakete jetzt vor der Legacy-Migration schützen?\n\n" +
      "Setzt bei allen Positionen, die schon einmal per Admin-UI editiert wurden, das Herkunfts-Flag auf \"manual\". " +
      "Damit sind Ihre Preise 100 % sicher vor versehentlichem Überschreiben durch die Legacy-Migration.\n\n" +
      "Idempotent — mehrfach klicken ist sicher.",
    )) return;
    setProtecting(true);
    try {
      const { data } = await api.post("/admin/wp-catalog/protect-manual-edits");
      setProtectResult(data);
      const total = (data.tenants || []).reduce(
        (s, t) => s + (t.components_protected || 0) + (t.packages_protected || 0), 0);
      toast.success(`${total} Positionen geschützt · Ihre Preise sind jetzt sicher.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schutz-Aktion fehlgeschlagen");
    } finally {
      setProtecting(false);
    }
  };

  const overallStyle = STATUS_STYLES[overall] || STATUS_STYLES.info;
  const errCount = checks.filter((c) => c.status === "error").length;
  const warnCount = checks.filter((c) => c.status === "warn").length;
  const okCount = checks.filter((c) => c.status === "ok").length;

  // Lead-Report: einmalige Zusammenfassungs-E-Mail über alle Telesales-Leads eines Tenants
  const [reportSending, setReportSending] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportForm, setReportForm] = useState({
    tenant_slug: "tuerkei-1",
    to: "christof@schirm.in",
    only_delivered: true,
  });
  const sendTelesalesReport = async ({ dryRun = false } = {}) => {
    if (!reportForm.tenant_slug.trim()) { toast.error("Tenant-Slug fehlt"); return; }
    if (!reportForm.to.includes("@")) { toast.error("Ungültige E-Mail-Adresse"); return; }
    if (!dryRun && !window.confirm(
      `Report jetzt echt an ${reportForm.to} senden?\n\nEnthält alle Telesales-Orientierungsangebote ` +
      `des Mandanten "${reportForm.tenant_slug}"${reportForm.only_delivered ? ", die bereits per E-Mail an den Kunden zugestellt wurden" : ""}.`,
    )) return;
    setReportSending(true);
    try {
      const { data } = await api.post("/admin/lead-reports/telesales-orientierungsangebote", {
        ...reportForm, dry_run: dryRun,
      });
      setReportResult(data);
      if (dryRun) {
        toast.info(`Vorschau: ${data.count} Angebot(e) würden versendet werden.`);
      } else if (data.sent) {
        toast.success(`E-Mail mit ${data.count} Angebot(en) an ${data.recipient} versendet.`);
      } else {
        toast.error(data.reason === "no-leads" ? data.message : `Versand fehlgeschlagen: ${data.delivery?.error || "unbekannt"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report-Versand fehlgeschlagen");
    } finally {
      setReportSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pb-24">
      <AppHeader title="System-Health" testid="admin-system-health" />
      <main className="mx-auto max-w-4xl px-3 sm:px-5 pt-4 space-y-4">
        <SignatureAlertsPanel />
        {/* Overall status hero */}
        <div className={`rounded-2xl border ${overallStyle.border} ${overallStyle.bg} p-4 sm:p-6`}
             data-testid="sh-overall">
          <div className="flex items-start gap-4">
            {overall === "ok" ? (
              <ShieldCheck size={44} className="text-emerald-400 shrink-0" />
            ) : (
              <ShieldAlert size={44} className={`${overallStyle.color} shrink-0`} />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] uppercase tracking-widest text-zinc-400 font-bold mb-1">
                System-Zustand
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-100">
                {overall === "ok" && "Alles im grünen Bereich"}
                {overall === "warn" && "Handlungsbedarf"}
                {overall === "error" && "Fehler erkannt"}
                {overall === "info" && "Systemstatus"}
              </h1>
              <p className="text-[12.5px] text-zinc-400 mt-1">
                {errCount > 0 && <><b className="text-red-300">{errCount}</b> Fehler · </>}
                {warnCount > 0 && <><b className="text-amber-300">{warnCount}</b> Warnung(en) · </>}
                <b className="text-emerald-300">{okCount}</b> OK
                {lastRun && (
                  <span className="text-zinc-500 ml-2">
                    · zuletzt geprüft: {lastRun.toLocaleTimeString("de-DE")}
                  </span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="sh-reload"
              className="h-9 px-3 shrink-0 rounded-lg bg-[#18181B] border border-zinc-700 hover:border-zinc-500 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50 self-start font-medium"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Neu prüfen
            </button>
          </div>
        </div>

        {/* Erklärung */}
        <div className="bg-[#0F0F11] border border-zinc-800 rounded-2xl p-3 text-[11.5px] text-zinc-400 leading-relaxed"
             data-testid="sh-intro">
          Dieses Dashboard prüft alle bekannten Deploy-Fallstricke. Fehler mit einem
          <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded bg-[#00E575] text-[#09090B] font-bold text-[12px]">
            <Zap size={10} /> Fixen
          </span>
          -Button lassen sich mit einem Klick idempotent lösen — mehrfach klicken ist sicher, manuelle Anpassungen werden respektiert.
        </div>

        {/* Manuelle Einträge schützen — VOR jeder Legacy-Migration einmalig ausführen */}
        <div className="bg-blue-500/10 border border-blue-500/40 rounded-2xl p-4"
             data-testid="sh-protect-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] uppercase tracking-widest text-blue-300 font-bold mb-1">
                🔒 Manuelle Einträge schützen (vor Legacy-Migration)
              </div>
              <p className="text-[12px] text-zinc-300 leading-relaxed font-medium">
                Wenn Sie in <b>WP-Komponenten</b> oder <b>WP-Pakete</b> bereits Preise manuell aktualisiert haben,
                klicken Sie hier <b>einmalig</b> — dann sind Ihre Preise gegen ein versehentliches Ausführen der
                Legacy-Migration abgesichert. Idempotent, gefahrlos wiederholbar.
              </p>
              {protectResult && (
                <div className="mt-2 text-[12px] text-emerald-300" data-testid="sh-protect-result">
                  ✓ Geschützt: {(protectResult.tenants || []).map(t =>
                    `${t.name}: ${t.components_protected} Komponenten + ${t.packages_protected} Pakete`
                  ).join(" · ")}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={protectManualEdits}
              disabled={protecting}
              data-testid="sh-protect-btn"
              className="h-10 px-4 shrink-0 rounded-lg bg-blue-500 text-white hover:bg-blue-600 font-bold text-[12px] uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {protecting ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Jetzt schützen
            </button>
          </div>
        </div>

        {/* Lead-Report: Telesales-Orientierungsangebote als E-Mail versenden */}
        <div className="bg-purple-500/10 border border-purple-500/40 rounded-2xl p-4"
             data-testid="sh-lead-report-panel">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] uppercase tracking-widest text-purple-300 font-bold mb-1">
                📧 Telesales-Orientierungsangebote als Report senden
              </div>
              <p className="text-[12px] text-zinc-300 leading-relaxed font-medium">
                Verschickt einmalig eine Übersichts-E-Mail mit allen Orientierungsangeboten,
                die von Telesales-Agents eines Mandanten erfasst wurden. Enthält je Lead:
                Kunde, PLZ, Empfehlung, Preis, Status, Zustellung.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <label className="block">
              <span className="block text-[12px] uppercase tracking-widest text-zinc-400 mb-1">Tenant-Slug</span>
              <input value={reportForm.tenant_slug}
                onChange={(e) => setReportForm({ ...reportForm, tenant_slug: e.target.value })}
                data-testid="sh-report-tenant"
                placeholder="tuerkei-1"
                className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-purple-500 outline-none font-mono" />
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[12px] uppercase tracking-widest text-zinc-400 mb-1">Empfänger-E-Mail</span>
              <input type="email" value={reportForm.to}
                onChange={(e) => setReportForm({ ...reportForm, to: e.target.value })}
                data-testid="sh-report-to"
                placeholder="christof@schirm.in"
                className="h-9 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-[13px] text-zinc-100 focus:border-purple-500 outline-none" />
            </label>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11.5px] text-zinc-300 inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={reportForm.only_delivered}
                onChange={(e) => setReportForm({ ...reportForm, only_delivered: e.target.checked })}
                data-testid="sh-report-only-delivered"
                className="h-4 w-4 accent-purple-500" />
              Nur wenn E-Mail an Kunde erfolgreich zugestellt wurde
            </label>
            <div className="flex gap-2 ml-auto">
              <button type="button" onClick={() => sendTelesalesReport({ dryRun: true })}
                disabled={reportSending}
                data-testid="sh-report-dryrun"
                className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-700 hover:border-zinc-500 text-[11.5px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 text-zinc-200 disabled:opacity-50">
                Vorschau
              </button>
              <button type="button" onClick={() => sendTelesalesReport({ dryRun: false })}
                disabled={reportSending}
                data-testid="sh-report-send"
                className="h-9 px-3 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-bold text-[11.5px] uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
                {reportSending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                Jetzt senden
              </button>
            </div>
          </div>
          {reportResult && (
            <div className="mt-3 text-[12px] text-zinc-300 bg-[#09090B] rounded-lg p-2 border border-zinc-800"
                 data-testid="sh-report-result">
              <div>
                <b>{reportResult.count}</b> Angebot(e) · Empfänger: <span className="font-mono">{reportResult.recipient}</span>
                {reportResult.sent && <span className="text-emerald-300 ml-2">✓ versendet</span>}
                {reportResult.dry_run && <span className="text-sky-300 ml-2">· Vorschau (nicht versendet)</span>}
              </div>
              {reportResult.sample?.length > 0 && (
                <div className="mt-1 text-zinc-500">
                  Beispiel-Zeilen: {reportResult.sample.map(s => `${s.name} · ${s.zip} · ${s.package}`).join(" | ")}
                </div>
              )}
            </div>
          )}
        </div>

        {loading && checks.length === 0 ? (
          <div className="text-center py-16 text-zinc-400 text-sm" data-testid="sh-loading">
            <Loader2 className="animate-spin inline mr-2" /> Prüfungen laufen…
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="sh-checks">
            {checks.map((c) => (
              <CheckCard
                key={c.id}
                check={c}
                onFix={runFix}
                fixingId={fixingId}
                onToggle={toggle}
                expanded={!!expanded[c.id]}
              />
            ))}
          </div>
        )}

        {/* Related Actions */}
        <div className="mt-6 rounded-2xl border border-zinc-800 bg-[#0F0F11] p-3 text-[11.5px]"
             data-testid="sh-related">
          <div className="uppercase tracking-widest text-zinc-500 font-bold mb-2">Weitere Werkzeuge</div>
          <div className="flex flex-wrap gap-2">
            <a href="/admin/wp-packages"
               className="h-8 px-3 rounded-lg bg-[#18181B] border border-zinc-700 hover:border-zinc-500 inline-flex items-center gap-1.5 text-zinc-200">
              <ExternalLink size={12} /> WP-Pakete
            </a>
            <a href="/admin/wp-components"
               className="h-8 px-3 rounded-lg bg-[#18181B] border border-zinc-700 hover:border-zinc-500 inline-flex items-center gap-1.5 text-zinc-200">
              <ExternalLink size={12} /> WP-Komponenten
            </a>
            <a href="/admin/tenants"
               className="h-8 px-3 rounded-lg bg-[#18181B] border border-zinc-700 hover:border-zinc-500 inline-flex items-center gap-1.5 text-zinc-200">
              <ExternalLink size={12} /> Mandanten-Editor
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
